/**
 * The settings screen's data shapes.
 *
 * Deliberately mirrors `backend/src/game/settings/settings.types.ts` rather
 * than importing from it — the two halves of this app do not share a package,
 * and the server clamps everything on write anyway, so the client's copy is a
 * convenience rather than an authority. The defaults must stay in step with
 * `config/audio-tuning.ts`'s `AUDIO_GAINS`; `settings-store.test.ts` asserts
 * that pairing so a retune cannot silently disagree with what the screen shows.
 */

export interface AudioSettings {
  master: number;
  music: number;
  sfx: number;
  ambient: number;
  ui: number;
  musicEnabled: boolean;
}

export interface GameplaySettings {
  mouseSensitivity: number;
  invertLook: boolean;
  damageNumbers: boolean;
  hitFlash: boolean;
  autoSkipFlyThrough: boolean;
  showPerformance: boolean;
}

export interface PlayerPreferences {
  audio: AudioSettings;
  gameplay: GameplaySettings;
}

export type KycStatus = 'NONE' | 'PENDING' | 'APPROVED' | 'REJECTED';

export interface SettingsOverview {
  profile: {
    email: string;
    emailVerified: boolean;
    pendingEmail: string | null;
    username: string | null;
    locale: string | null;
    currency: string | null;
    firstName: string | null;
    lastName: string | null;
    billing: {
      street: string | null;
      city: string | null;
      zip: string | null;
      country: string | null;
    };
    memberSince: string;
  };
  kyc: {
    status: KycStatus;
    fullName: string | null;
    country: string | null;
    submittedAt: string | null;
    reviewedAt: string | null;
    rejectReason: string | null;
  };
  preferences: PlayerPreferences;
  features: { kyc: boolean; withdrawals: boolean; invoices: boolean };
}

export interface PaymentEntry {
  kind: 'PAYMENT' | 'INVOICE';
  id: string;
  at: string;
  description: string;
  provider: string | null;
  amountCents: number;
  currency: string;
  status: string;
  invoiceNumber: string | null;
}

/**
 * One row of `GET /api/invoices` — a document, not a transaction.
 *
 * Deposits and withdrawals both land here, which is the point: an invoice is
 * the record of money moving in either direction, and a player looking for
 * "the paperwork" should not have to know which list it lives in.
 */
export interface InvoiceRow {
  id: string;
  number: string;
  /** IN = a deposit. OUT = a payout. */
  type: 'IN' | 'OUT';
  amountCents: number;
  vatCents: number;
  totalCents: number;
  currency: string;
  description: string;
  status: string;
  createdAt: string;
}

export interface WithdrawalRequestRow {
  id: string;
  amountCents: number;
  currency: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  payoutMethod: string | null;
  reason: string | null;
  createdAt: string;
  reviewedAt: string | null;
}

export interface WithdrawalPayoutMethod {
  id: string;
  label: string;
  receiver: 'cardNumber' | 'iban';
  currencies: string[];
}

export interface WithdrawalOverview {
  availableCents: number;
  pendingCents: number;
  minimumCents: number;
  emberBalance: number;
  premiumPerEur: number;
  currency: string;
  kycStatus: KycStatus;
  /** False when the operator has verification switched off — then core does not ask for it. */
  kycRequired?: boolean;
  enabled: boolean;
  requests: WithdrawalRequestRow[];
  methods: WithdrawalPayoutMethod[];
}

export const MOUSE_SENSITIVITY_MIN = 0.25;
export const MOUSE_SENSITIVITY_MAX = 3;

export const DEFAULT_AUDIO: AudioSettings = {
  master: 0.8,
  music: 0.45,
  sfx: 0.9,
  ambient: 0.35,
  ui: 0.6,
  musicEnabled: true,
};

export const DEFAULT_GAMEPLAY: GameplaySettings = {
  mouseSensitivity: 1,
  invertLook: false,
  damageNumbers: true,
  hitFlash: true,
  autoSkipFlyThrough: false,
  showPerformance: false,
};

export function defaultPreferences(): PlayerPreferences {
  return { audio: { ...DEFAULT_AUDIO }, gameplay: { ...DEFAULT_GAMEPLAY } };
}

function clamp(value: unknown, min: number, max: number, fallback: number): number {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, n));
}

function bool(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

function asRecord(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

/**
 * Same clamping as the server, applied to whatever comes out of localStorage.
 *
 * A cached blob written by an older build — or edited by hand — must not be
 * able to deafen somebody on the frame before the fetch lands.
 */
export function coercePreferences(raw: unknown): PlayerPreferences {
  const v = asRecord(raw);
  const a = asRecord(v.audio);
  const g = asRecord(v.gameplay);
  return {
    audio: {
      master: clamp(a.master, 0, 1, DEFAULT_AUDIO.master),
      music: clamp(a.music, 0, 1, DEFAULT_AUDIO.music),
      sfx: clamp(a.sfx, 0, 1, DEFAULT_AUDIO.sfx),
      ambient: clamp(a.ambient, 0, 1, DEFAULT_AUDIO.ambient),
      ui: clamp(a.ui, 0, 1, DEFAULT_AUDIO.ui),
      musicEnabled: bool(a.musicEnabled, DEFAULT_AUDIO.musicEnabled),
    },
    gameplay: {
      mouseSensitivity: clamp(
        g.mouseSensitivity,
        MOUSE_SENSITIVITY_MIN,
        MOUSE_SENSITIVITY_MAX,
        DEFAULT_GAMEPLAY.mouseSensitivity,
      ),
      invertLook: bool(g.invertLook, DEFAULT_GAMEPLAY.invertLook),
      damageNumbers: bool(g.damageNumbers, DEFAULT_GAMEPLAY.damageNumbers),
      hitFlash: bool(g.hitFlash, DEFAULT_GAMEPLAY.hitFlash),
      autoSkipFlyThrough: bool(g.autoSkipFlyThrough, DEFAULT_GAMEPLAY.autoSkipFlyThrough),
      showPerformance: bool(g.showPerformance, DEFAULT_GAMEPLAY.showPerformance),
    },
  };
}
